import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2, CheckCircle2, AlertCircle, Smartphone } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useMutation } from "@tanstack/react-query";

interface OTPVerificationProps {
  phone: string;
  onVerified: () => void;
  onCancel?: () => void;
}

export function OTPVerification({ phone, onVerified, onCancel }: OTPVerificationProps) {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const [otpSent, setOtpSent] = useState(false);
  const otpContainerRef = useRef<HTMLDivElement>(null);

  const handleApiCall = async (call: () => Promise<Response>) => {
    try {
      const response = await call();
      return await response.json();
    } catch (error: any) {
      return {
        success: false,
        error: error?.message || "Failed to communicate with OTP server",
      };
    }
  };

  // Countdown timer for resend button
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendCountdown]);

  // Scroll to and highlight OTP input when code is sent
  useEffect(() => {
    if (otpSent && otpContainerRef.current) {
      // Smooth scroll to the OTP input
      otpContainerRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center' 
      });
    }
  }, [otpSent]);

  // Verify OTP mutation
  const verifyMutation = useMutation({
    mutationFn: async (otpCode: string) => {
      return handleApiCall(() =>
        apiRequest("POST", "/api/auth/verify-otp", {
          body: { otp: otpCode },
        })
      );
    },
    onSuccess: (data) => {
      if (data.success) {
        setSuccess(true);
        setError("");
        setTimeout(() => {
          onVerified();
        }, 1500);
      } else {
        setError(data.error || "Invalid OTP code");
        setOtp("");
      }
    },
    onError: (error: any) => {
      setError(error.message || "Failed to verify OTP");
      setOtp("");
    },
  });

  // Send OTP mutation (first time)
  const sendMutation = useMutation({
    mutationFn: async () => {
      return handleApiCall(() =>
        apiRequest("POST", "/api/auth/send-otp", {
          body: { phone },
        })
      );
    },
    onSuccess: (data) => {
      if (data.success) {
        setError("");
        setOtp("");
        setOtpSent(true);
        setResendCountdown(60);
        setCanResend(false);
      } else {
        setError(data.error || "Failed to send OTP");
      }
    },
    onError: (error: any) => {
      setError(error.message || "Failed to send OTP");
    },
  });

  // Resend OTP mutation
  const resendMutation = useMutation({
    mutationFn: async () => {
      return handleApiCall(() =>
        apiRequest("POST", "/api/auth/resend-otp", {
          body: { phone },
        })
      );
    },
    onSuccess: (data) => {
      if (data.success) {
        setError("");
        setOtp("");
        setResendCountdown(60);
        setCanResend(false);
      } else {
        setError(data.error || "Failed to resend OTP");
      }
    },
    onError: (error: any) => {
      setError(error.message || "Failed to resend OTP");
    },
  });

  // Auto-submit when OTP is complete (only if OTP was already sent)
  useEffect(() => {
    if (otp.length === 6 && !verifyMutation.isPending && otpSent) {
      verifyMutation.mutate(otp);
    }
  }, [otp, otpSent]);

  const handleSendOtp = () => {
    if (!sendMutation.isPending) {
      sendMutation.mutate();
    }
  };

  const handleResend = () => {
    if (canResend && !resendMutation.isPending) {
      resendMutation.mutate();
    }
  };

  return (
    <div className="w-full max-w-md mx-auto space-y-6 p-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <Smartphone className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Verify Your Phone Number
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {otpSent ? (
            <>
              We've sent a 6-digit code to
              <br />
              <span className="font-semibold text-gray-900 dark:text-white">{phone}</span>
            </>
          ) : (
            <>
              Click 'Send Code' to receive a verification code at
              <br />
              <span className="font-semibold text-gray-900 dark:text-white">{phone}</span>
            </>
          )}
        </p>
      </div>

      {/* OTP Input */}
      <div className="space-y-4">
        <div 
          ref={otpContainerRef}
          className={`
            flex justify-center p-4 rounded-lg transition-all duration-300
            ${otpSent && !success ? 'bg-primary/5 ring-2 ring-primary/20 animate-pulse-subtle' : ''}
          `}
        >
          <InputOTP 
            maxLength={6} 
            value={otp} 
            onChange={setOtp}
            disabled={verifyMutation.isPending || success || !otpSent}
            autoFocus={otpSent}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>

        {/* Helpful prompt when OTP is sent */}
        {otpSent && !success && !error && (
          <div className="text-center animate-fade-in">
            <p className="text-sm font-medium text-primary flex items-center justify-center gap-2">
              <span className="text-lg">👆</span>
              Enter your 6-digit code above
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Success Message */}
        {success && (
          <Alert className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
            <AlertDescription className="text-green-800 dark:text-green-200">
              Phone verified successfully! Redirecting...
            </AlertDescription>
          </Alert>
        )}

        {/* Verification Status */}
        {verifyMutation.isPending && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Verifying code...</span>
          </div>
        )}
      </div>

      {/* Send/Resend Section */}
      <div className="space-y-3">
        <div className="text-center">
          {!otpSent ? (
            <Button
              onClick={handleSendOtp}
              disabled={sendMutation.isPending}
              data-testid="button-send-otp"
            >
              {sendMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Code"
              )}
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResend}
              disabled={!canResend || resendMutation.isPending}
              data-testid="button-resend-otp"
            >
              {resendMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : canResend ? (
                "Resend Code"
              ) : (
                `Resend code in ${resendCountdown}s`
              )}
            </Button>
          )}
        </div>

        {onCancel && (
          <div className="text-center">
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={verifyMutation.isPending || success}
              data-testid="button-cancel-verification"
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="text-center space-y-2">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Didn't receive the code? Check your phone or try resending.
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Code expires in 10 minutes.
        </p>
      </div>
    </div>
  );
}
